import React, { useCallback, useEffect, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Loader2, Send, ShieldCheck, X, Trash2, AlertTriangle,
} from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ADJUSTMENT_STATUS_META, ADJUSTMENT_TYPE_META, RECIPIENT_TYPE_LABEL, money, fmtDate, TOASTS } from '@/lib/accountingConstants';

export default function AdjustmentDetailDrawer({ adjustmentId, isKR, onClose }) {
  const [adj, setAdj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancel, setShowCancel] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await accountingAPI.getAdjustment(adjustmentId);
      setAdj(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load adjustment'); }
    finally { setLoading(false); }
  }, [adjustmentId]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setAdj(data);
      toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || TOASTS.ACTION_FAILED);
    } finally { setBusy(false); }
  };

  const doSubmit = () => run(() => accountingAPI.submitAdjustment(adjustmentId), TOASTS.SUBMITTED);
  const doApprove = () => run(() => accountingAPI.approveAdjustment(adjustmentId), TOASTS.ADJUSTMENT_POSTED);

  const doCancel = async () => {
    if (cancelReason.trim().length < 3) return toast.error('Reason required');
    await run(() => accountingAPI.cancelAdjustment(adjustmentId, { reason: cancelReason.trim() }), TOASTS.CANCELLED);
    setShowCancel(false); setCancelReason('');
  };

  const doDelete = async () => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    setBusy(true);
    try {
      await accountingAPI.deleteAdjustment(adjustmentId);
      toast.success(TOASTS.ADJUSTMENT_DELETED);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || TOASTS.ACTION_FAILED); }
    finally { setBusy(false); }
  };

  const meta = adj ? ADJUSTMENT_STATUS_META[adj.status] : ADJUSTMENT_STATUS_META.draft;
  const Icon = meta.icon;

  const canSubmit = adj?.status === 'draft';
  const canApprove = adj?.status === 'pending_approval' && isKR;
  const canCancel = ['draft', 'pending_approval'].includes(adj?.status);
  const canDelete = adj?.status === 'draft';

  return (
    <Sheet open={!!adjustmentId} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-[560px] sm:max-w-[560px] overflow-y-auto" data-testid="adj-detail-drawer">
        <SheetHeader>
          <SheetTitle className="font-mono text-base">{adj?.adjustment_number || '…'}</SheetTitle>
          <SheetDescription>
            {adj ? `${ADJUSTMENT_TYPE_META[adj.adjustment_type]?.label} · ${adj.recipient_name} · ${money(adj.amount)}` : 'Loading…'}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="mt-8 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : !adj ? null : (
          <div className="mt-4 space-y-4">
            {/* Status */}
            <div className="flex items-center gap-2">
              <Badge className={`${meta.tone} border font-normal text-sm`} data-testid="adj-status">
                <Icon className="h-3.5 w-3.5 mr-1 inline" /> {meta.label}
              </Badge>
              <div className="text-xs text-slate-500">Created {fmtDate(adj.created_at)} by <b>{adj.created_by_name}</b></div>
            </div>

            {/* Blocked warning */}
            {adj.recipient_blocked && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2" data-testid="blocked-warning">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <b>Recipient is blocked for automatic payments</b> — {adj.blocking_reasons?.join(', ')}.
                  {adj.adjustment_type === 'manual_payment' && ' KR approval will be logged as `payment_to_blocked_recipient`.'}
                </div>
              </div>
            )}

            {/* Detail grid */}
            <div className="rounded-md border p-4 space-y-2 text-sm">
              <Row label="Type">{ADJUSTMENT_TYPE_META[adj.adjustment_type]?.label}</Row>
              <Row label="Recipient">{adj.recipient_name} <span className="text-xs uppercase text-slate-500 ml-1">({RECIPIENT_TYPE_LABEL[adj.recipient_type] || adj.recipient_type})</span></Row>
              <Row label="Amount">{money(adj.amount)}</Row>
              <Row label="Reason">{adj.reason}</Row>
              {adj.notes && <Row label="Notes">{adj.notes}</Row>}
              {adj.adjustment_type === 'manual_payment' && (
                <>
                  <Row label="Payment Date">{adj.payment_date}</Row>
                  <Row label="Method">{(adj.payment_method || '').toUpperCase()}</Row>
                  {adj.payment_reference && <Row label="Reference">{adj.payment_reference}</Row>}
                </>
              )}
              {adj.posted_at && <Row label="Posted">{fmtDate(adj.posted_at)} by {adj.posted_by_name}</Row>}
              {adj.ledger_entry_id && <Row label="Ledger Entry">{adj.ledger_entry_id.slice(0, 8)}…</Row>}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {canSubmit && (
                <Button size="sm" onClick={doSubmit} disabled={busy} data-testid="submit-adj-btn">
                  <Send className="h-4 w-4 mr-1.5" /> Submit for Approval
                </Button>
              )}
              {canApprove && (
                <Button size="sm" onClick={doApprove} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-adj-btn">
                  <ShieldCheck className="h-4 w-4 mr-1.5" /> Approve + Post
                </Button>
              )}
              {canCancel && (
                <Button size="sm" variant="destructive" onClick={() => setShowCancel(true)} disabled={busy} data-testid="cancel-adj-btn">
                  <X className="h-4 w-4 mr-1.5" /> Cancel
                </Button>
              )}
              {canDelete && (
                <Button size="sm" variant="ghost" onClick={doDelete} disabled={busy} data-testid="delete-adj-btn">
                  <Trash2 className="h-4 w-4 mr-1.5" /> Delete Draft
                </Button>
              )}
            </div>

            {/* Cancel form inline */}
            {showCancel && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-2">
                <Label>Cancel reason (required)</Label>
                <Textarea rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setShowCancel(false)}>Keep</Button>
                  <Button size="sm" variant="destructive" onClick={doCancel} disabled={busy} data-testid="confirm-cancel-adj-btn">Confirm Cancel</Button>
                </div>
              </div>
            )}

            {/* Status history */}
            <div className="rounded-md border p-4 space-y-2">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">Status History</div>
              <ol className="space-y-1.5">
                {(adj.status_history || []).map((h, i) => {
                  const m = ADJUSTMENT_STATUS_META[h.to_status] || { label: h.to_status, tone: 'bg-slate-100 text-slate-700 border-slate-300' };
                  return (
                    <li key={i} className="flex items-start gap-2 text-xs">
                      <Badge className={`${m.tone} border font-normal shrink-0`}>{m.label}</Badge>
                      <div className="flex-1">
                        <div>{h.by_name} · <span className="text-slate-500">{fmtDate(h.at)}</span></div>
                        {h.reason && <div className="italic text-slate-600">"{h.reason}"</div>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-32 text-xs uppercase tracking-wide text-slate-500 shrink-0 pt-0.5">{label}</div>
      <div className="flex-1 text-slate-800">{children}</div>
    </div>
  );
}
